import * as yup from 'yup';
import {
  EmploymentType,
  WorkType,
} from '../interfaces/user-work-experience.interface';
import { normalizeDateValue } from '../utils/helper/normalizeDateValue';

const today = new Date();

export const workExperienceSchema = yup.object().shape({
  id: yup.string().optional(),

  isActiveAthlete: yup.boolean().default(false),

  title: yup
    .string()
    .min(2, 'The title must be at least 2 characters long.')
    .max(50, 'The title must be at most 50 characters long.')
    .required('Title is required.')
    .matches(/^[A-Za-z\s]+$/, 'The title can only contain letters and spaces.'),

  employmentType: yup
    .mixed<EmploymentType>()
    .oneOf(Object.values(EmploymentType), 'Please select a valid type')
    .required('Employment type is required'),
  companyName: yup
    .string()
    .min(2, 'The company name must be at least 2 characters long.')
    .max(100, 'The company name must be at most 100 characters long.')
    .required('Company name is required.')
    .matches(
      /^[A-Za-z0-9\s_\-.,''?|()/&]+$/,
      'The company name can only contain letters, numbers, spaces, underscores, hyphens, dots, apostrophes, and question marks.'
    ),

  location: yup
    .string()
    .min(2, 'The location must be at least 2 characters long.')
    .max(100, 'The location must be at most 100 characters long.')
    .required('Location is required.')
    .matches(
      /^[A-Za-z0-9\s_\-.,''?|()/&]+$/,
      'The location can only contain letters, numbers, spaces, underscores, hyphens, dots, apostrophes, and question marks.'
    ),
  workType: yup
    .mixed<WorkType>()
    .oneOf(Object.values(WorkType), 'Please select a valid type')
    .required('Employment type is required'),

  responsibilities: yup
    .string()
    .nullable()
    .notRequired()
    .min(10, 'Responsibilities should be at least 10 characters long.')
    .max(1000, 'Responsibilities should be at most 1000 characters long.'),

  startDate: yup
    .date()
    .transform((value, originalValue) =>
      normalizeDateValue(value, originalValue)
    )
    .typeError('Start date must be a valid date')
    .required('Start date is required')
    .max(today, 'Start date cannot be in the future'),

  endDate: yup
    .date()
    .transform((value, originalValue) =>
      normalizeDateValue(value, originalValue)
    )
    .typeError('End date must be a valid date')
    .when('activeWork', {
      is: true,
      then: (schema) => schema.optional().nullable(),
      otherwise: (schema) =>
        schema
          .required('End date is required')
          .max(today, 'End date cannot be in the future')
          .min(yup.ref('startDate'), 'End date must be greater than start date')
          .test(
            'is-greater',
            'End date must be greater than start date',
            function (value) {
              const { startDate } = this.parent;
              if (!value || !startDate) return true;
              return value > startDate;
            }
          ),
    }),

  activeWork: yup.boolean().default(false),
});

export const workExperiencesSchema = yup.object().shape({
  workExperiences: yup
    .array()
    .of(workExperienceSchema)
    .min(1, 'At least one work experience is required.')
    .required('Work experience is required.'),
});
